import { Link } from "react-router-dom";
import { TerraLogo } from "@/components/TerraLogo";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Logo + optional title block as one link. Optional tooltip (default "Dashboard").
 * Pass `tooltip={false}` for no tooltip (e.g. /tools → landing).
 *
 * @param {string} to - React Router path
 * @param {string | false | null} [tooltip] - false/null = no tooltip; default "Dashboard"
 * @param {string} [logoClassName] - classes on the logo image
 * @param {string} [size] - TerraLogo preset: sm | md | lg
 * @param {string} [className] - extra classes on the link (e.g. mx-auto)
 * @param {React.ReactNode} [children] - title / subtitle next to logo
 */
export function BrandedHomeLink({
  to,
  tooltip = "Dashboard",
  logoClassName,
  size,
  className = "",
  children,
}) {
  const showTooltip = tooltip !== false && tooltip !== null;
  const tooltipText = typeof tooltip === "string" ? tooltip : "Dashboard";

  const link = (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 rounded-md hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 min-w-0",
        className
      )}
      aria-label={showTooltip ? tooltipText : "Home"}
    >
      <TerraLogo className={logoClassName} size={size} />
      {children}
    </Link>
  );

  if (!showTooltip) {
    return link;
  }

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="bg-muted text-foreground border border-border"
        >
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default BrandedHomeLink;
